import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { TariffProfile } from '../../interfaces/route-planning/gtfs/tariff-profile.interface';
import { ScheduleRepository } from '../../repositories/schedule.repository';
import { IntercityLines } from '../../interfaces/route-planning/gtfs/intercity-lines.interface';
import {
  GetIntercityLinesRequest,
  RouteStops,
  StopFullData,
  StopMessagesForDayResponse,
  StopTimesAtStopResponse,
  StopTimesWithRoute,
} from '../../interfaces/route-planning/service/@schedule-service.interface';
import { Route } from '../../interfaces/route-planning/gtfs/route.interface';
import { Stop } from '../../interfaces/route-planning/gtfs/stop.interface';
import { SpecialMessage } from '../../interfaces/route-planning/gtfs/special-messages/special-message.interface';
import { Coordinate } from '../../interfaces/location/coordinate.interface';
import { Price } from '../../interfaces/route-planning/price.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { MultiLanguage } from '../../interfaces/multi-langauge.interface';

@Injectable({
  providedIn: 'root',
})
export class ScheduleService {
  constructor(private readonly scheduleRepository: ScheduleRepository, private readonly languageService: LanguageService) { }

  getTariffProfiles(): Observable<SelectOption<TariffProfile>[]> {
    const language = this.languageService.getSelectedLanguage();
    return this.scheduleRepository.getTariffProfiles().pipe(
      map<ServiceResponse<TariffProfile[]>, SelectOption<TariffProfile>[]>(
        (profiles) =>
          profiles.data.map((profile) => ({
            label: profile.profileName[language.symbol as keyof MultiLanguage] || profile.profileName.he,
            value: profile,
          }))
      )
    );
  }

  getRoutePrice(
    model: {
      routeId: number,
      startingStopId: number,
      endingStopId: number
    }
  ): Observable<ServiceResponse<Price[]>> {
    return this.scheduleRepository.getRoutePrice(model);
  }

  getIntercityLines(
    model: GetIntercityLinesRequest
  ): Observable<ServiceResponse<IntercityLines[]>> {
    return this.scheduleRepository.getInterCityLines(model);
  }

  searchLine(lineTerm: string): Observable<ServiceResponse<Route[]>> {
    return this.scheduleRepository.searchLine(lineTerm);
  }

  searchStation(stationTerm: string): Observable<ServiceResponse<Stop[]>> {
    return this.scheduleRepository.searchStation(stationTerm);
  }

  getStopFullData(stopCode: number): Observable<ServiceResponse<StopFullData>> {
    return this.scheduleRepository.getStopFullData(stopCode);
  }

  getStopByCode(stopcode: number): Observable<ServiceResponse<Stop>> {
    return this.scheduleRepository.getStopByCode(stopcode);
  }

  getRouteFullData(routeId: number): Observable<ServiceResponse<Route>> {
    return this.scheduleRepository.getRouteFullData(routeId);
  }

  getRouteStops(
    routeId: number,
    oppositeDirectionId: number
  ): Observable<
    ServiceResponse<{ route: RouteStops; oppositeDirectionRoute: RouteStops }>
  > {
    return this.scheduleRepository.getRouteStops(routeId, oppositeDirectionId);
  }

  getAllTrainStations(): Observable<ServiceResponse<Stop[]>> {
    return this.scheduleRepository.getAllTrainStations();
  }

  getSpecialMessageByRouteDesc(
    routeDesc: string
  ): Observable<ServiceResponse<SpecialMessage[]>> {
    return this.scheduleRepository.getSpecialMessageByRouteDesc(routeDesc);
  }

  GetSpecialMessageByManyRouteDesc(
    routeDescs: string[]
  ): Observable<ServiceResponse<SpecialMessage[]>> {
    return this.scheduleRepository.GetSpecialMessageByManyRouteDesc(routeDescs);
  }

  getSpecialMessageByStopCodes(
    stopCodes: string[],
    dayPassingAtStop?: number,
    timePassingAtStop?: string
  ): Observable<
    ServiceResponse<{ stopId: number; specialMessages: SpecialMessage[] }[]>
  > {
    return this.scheduleRepository.getSpecialMessageByStopCodes(
      stopCodes,
      dayPassingAtStop,
      timePassingAtStop
    );
  }

  getStopsCloseToLocation(coordinates: Coordinate): Observable<ServiceResponse<StopFullData[]>> {
    return this.scheduleRepository.getStopsCloseToLocation(coordinates);
  }

  getClosestStop(coordinates: Coordinate): Observable<ServiceResponse<StopFullData>> {
    return this.scheduleRepository.getClosestStop(coordinates);
  }

  getRouteLocations(routeId: number): Observable<ServiceResponse<Coordinate[]>> {
    return this.scheduleRepository.getRouteLocations(routeId);
  }

  getNextRouteLocation(routeDesc: string, stopCode: string): Observable<ServiceResponse<Coordinate>> {
    return this.scheduleRepository.getNextRouteLocation(routeDesc, stopCode);
  }

  getRoutePolylineByRouteDescStopAndTime(
    routeDesc: string,
    stopCode: string,
    time: string
  ): Observable<ServiceResponse<string>> {
    return this.scheduleRepository.getRoutePolylineByRouteDescStopAndTime(
      routeDesc,
      stopCode,
      time
    );
  }

  getStopTimesAtStop(stopCode: number, day: Date): Observable<ServiceResponse<StopTimesAtStopResponse>> {
    return this.scheduleRepository.getStopTimesAtStop(stopCode, day);
  }

  getStopMessagesForTheDay(
    stopCode: number,
    day: Date
  ): Observable<ServiceResponse<StopMessagesForDayResponse>> {
    return this.scheduleRepository.getStopMessagesForTheDay(stopCode, day);
  }

  checkIfRouteHasAlternatives(routeId: number): Observable<ServiceResponse<boolean>> {
    return this.scheduleRepository.checkIfRouteHasAlternatives(routeId);
  }

  getTripShapePolyline(tripId: string): Observable<ServiceResponse<string>> {
    return this.scheduleRepository.getTripShapePolyline(tripId);
  }
}
